import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Building2,
  Phone,
  FileText,
  Upload,
  Image,
  Bold,
  Italic,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  Download,
  Database
} from "lucide-react";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

// Componente de barra de herramientas para el editor
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const colors = ['#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777'];

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-slate-50 rounded-t-md">
      <Button
        type="button"
        variant={editor.isActive('bold') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('italic') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className="h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <Button
        type="button"
        variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className="h-8 w-8 p-0"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className="h-8 w-8 p-0"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className="h-8 w-8 p-0"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className="h-8 w-8 p-0"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className="h-8 w-8 p-0"
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <div className="flex items-center gap-1">
        <Palette className="h-4 w-4 text-slate-500" />
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => editor.chain().focus().setColor(color).run()}
            className={`w-5 h-5 rounded border border-slate-300 hover:scale-110 transition-transform ${
              editor.isActive('textStyle', { color }) ? 'ring-2 ring-offset-1 ring-blue-500' : ''
            }`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  );
};

const SettingsAgencia = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const isAdmin = user?.role === "admin";
  const [gmailSenders, setGmailSenders] = useState([]);
  const [settings, setSettings] = useState({
    name: "",
    rfc: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    tax_id: "",
    logo_url: "",
    sidebar_logo_url: "",
    internal_notifications_sender_user_id: "",
    internal_notifications_from_alias: "",
    internal_notifications_from_name: "",
    default_terms: "",
    default_currency: "USD",
    business_units: []
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color
    ],
    content: settings.default_terms || '',
    onUpdate: ({ editor }) => {
      setSettings(prev => ({ ...prev, default_terms: editor.getHTML() }));
    }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Merge with defaults so we don't lose fields the backend didn't return yet
        // (e.g. when adding new settings fields the existing settings doc may not have them)
        setSettings((prev) => ({ ...prev, ...(res.data || {}) }));
        if (editor && res.data.default_terms) {
          editor.commands.setContent(res.data.default_terms);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("Error al cargar configuración");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    // Load Gmail-connected mailboxes (for the internal notifications sender selector)
    axios.get(`${API}/form-schemas/_gmail-senders/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setGmailSenders(r.data || []))
      .catch(() => {});
  }, [token, editor]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Re-merge response so confirmation reflects what was actually persisted
      setSettings((prev) => ({ ...prev, ...(res.data || {}) }));
      toast.success("Configuración guardada");
    } catch (error) {
      console.error("Error saving settings:", error);
      const detail = error?.response?.data?.detail || error?.message || "Error desconocido";
      toast.error(`Error al guardar: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecciona una imagen");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/upload/logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setSettings(prev => ({ ...prev, logo_url: res.data.url }));
      toast.success("Logo subido exitosamente");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `backup_pdtravel_${new Date().toISOString().slice(0,10)}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup descargado exitosamente");
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Error al descargar el backup");
    } finally {
      setDownloadingBackup(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-agencia-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Datos de la Agencia
              </h1>
              <p className="text-slate-500">Información que aparecerá en las cotizaciones</p>
            </div>
          </div>
          <Button
            className="action-button"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Agency Info */}
          <Card data-testid="agency-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-slate-600" />
                Información de la Agencia
              </CardTitle>
              <CardDescription>
                Estos datos aparecerán en las cotizaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Agencia</Label>
                  <Input
                    id="name"
                    value={settings.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Mi Agencia de Viajes"
                    data-testid="agency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rfc">RFC</Label>
                  <Input
                    id="rfc"
                    value={settings.rfc}
                    onChange={(e) => updateField("rfc", e.target.value)}
                    placeholder="XAXX010101000"
                    data-testid="rfc-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={settings.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  placeholder="Calle, Número, Colonia, Ciudad, CP"
                  data-testid="address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card data-testid="logo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-slate-600" />
                Logos de la Agencia
              </CardTitle>
              <CardDescription>
                Sube o ingresa las URLs de tus logos para el menú y las cotizaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="sidebar_logo_url">Logo del Menú Principal</Label>
                <p className="text-xs text-slate-500 mb-2">Este logo aparecerá en el sidebar/menú lateral de la aplicación</p>
                <Input
                  id="sidebar_logo_url"
                  value={settings.sidebar_logo_url}
                  onChange={(e) => updateField("sidebar_logo_url", e.target.value)}
                  placeholder="https://example.com/logo-menu.png"
                  data-testid="sidebar-logo-url-input"
                />
                {settings.sidebar_logo_url && (
                  <div className="p-4 bg-slate-900 rounded-lg inline-block mt-2">
                    <p className="text-xs text-slate-400 mb-2">Vista previa (tema oscuro):</p>
                    <img
                      src={settings.sidebar_logo_url}
                      alt="Sidebar logo preview"
                      className="h-10 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <Separator />

              {/* Internal notifications sender mailbox */}
              <div className="space-y-2">
                <Label htmlFor="internal_sender">Buzón remitente para notificaciones internas</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Cuenta Gmail desde la que se enviarán los correos automáticos del sistema:
                  asignación de tickets, transferencias y comentarios. Si no se selecciona,
                  el correo sale desde la cuenta del usuario que dispara la acción.
                </p>
                <select
                  id="internal_sender"
                  value={settings.internal_notifications_sender_user_id || ""}
                  onChange={(e) => updateField("internal_notifications_sender_user_id", e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="internal-sender-select"
                >
                  <option value="">— Usar cuenta del usuario que envía —</option>
                  {gmailSenders.map(g => (
                    <option key={g.user_id} value={g.user_id}>
                      {(g.user_name || g.user_email || g.gmail_email)} · ✉ {g.gmail_email}
                    </option>
                  ))}
                </select>
                {gmailSenders.length === 0 && (
                  <p className="text-xs text-amber-600">
                    ⚠ Aún no hay cuentas Gmail conectadas. Conecta Gmail desde el ícono de Gmail en el sidebar.
                  </p>
                )}
              </div>

              {/* Alias remitente para correos internos */}
              <div className="space-y-2">
                <Label htmlFor="internal_alias">Alias remitente "From:" (solo para tickets internos TKT-)</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Se usa solo para los correos automáticos de tickets internos/soporte (folio <code className="bg-slate-100 px-1 rounded">TKT-</code>):
                  asignación, transferencia, cierre, etc.
                  Los tickets de cotización (folio <code className="bg-slate-100 px-1 rounded">COT-</code>) <strong>siempre</strong> salen
                  desde la dirección principal del buzón seleccionado arriba.<br />
                  El alias debe estar agregado y <strong>verificado</strong> en Gmail del buzón seleccionado
                  (Gmail → Configuración → Cuentas → "Enviar como"). Si no existe ahí, el correo saldrá desde la dirección principal.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="internal_alias_name" className="text-xs text-slate-600">Nombre visible</Label>
                    <Input
                      id="internal_alias_name"
                      value={settings.internal_notifications_from_name || ""}
                      onChange={(e) => updateField("internal_notifications_from_name", e.target.value)}
                      placeholder="Helpdesk PD Travel"
                      data-testid="internal-alias-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="internal_alias" className="text-xs text-slate-600">Email del alias</Label>
                    <Input
                      id="internal_alias"
                      type="email"
                      value={settings.internal_notifications_from_alias || ""}
                      onChange={(e) => updateField("internal_notifications_from_alias", e.target.value)}
                      placeholder="helpdesk@pdtravelmex.com"
                      data-testid="internal-alias-input"
                    />
                  </div>
                </div>
                {(settings.internal_notifications_from_alias && settings.internal_notifications_from_name) && (
                  <p className="text-xs text-emerald-700 mt-1">
                    ✓ Vista previa del From: <code className="bg-emerald-50 px-1 rounded">"{settings.internal_notifications_from_name}" &lt;{settings.internal_notifications_from_alias}&gt;</code>
                  </p>
                )}
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Logo para Cotizaciones/PDF</Label>
                <p className="text-xs text-slate-500">Este logo aparecerá en las cotizaciones y documentos PDF</p>
                
                {/* Upload Button */}
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadLogo}
                      className="hidden"
                      disabled={uploading}
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Upload className="w-4 h-4" />
                      {uploading ? "Subiendo..." : "Subir Logo"}
                    </div>
                  </label>
                  <span className="text-sm text-slate-500">o</span>
                  <Input
                    value={settings.logo_url}
                    onChange={(e) => updateField("logo_url", e.target.value)}
                    placeholder="Pega una URL de imagen"
                    className="flex-1"
                    data-testid="logo-url-input"
                  />
                </div>
                
                {/* Preview */}
                {settings.logo_url && (
                  <div className="p-4 bg-slate-50 rounded-lg mt-2">
                    <p className="text-xs text-slate-500 mb-2">Vista previa:</p>
                    <img
                      src={settings.logo_url.startsWith("/api") ? `${window.location.origin}${settings.logo_url}` : settings.logo_url}
                      alt="Logo preview"
                      className="h-16 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card data-testid="contact-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-slate-600" />
                Contacto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={settings.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="+52 55 1234 5678"
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="contacto@miagencia.com"
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Sitio Web</Label>
                <Input
                  id="website"
                  value={settings.website}
                  onChange={(e) => updateField("website", e.target.value)}
                  placeholder="https://www.miagencia.com"
                  data-testid="website-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Default Settings */}
          <Card data-testid="defaults-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Configuración por Defecto
              </CardTitle>
              <CardDescription>
                Valores predeterminados para nuevas cotizaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Moneda por Defecto</Label>
                <Select
                  value={settings.default_currency}
                  onValueChange={(v) => updateField("default_currency", v)}
                >
                  <SelectTrigger data-testid="default-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - Dólar Estadounidense</SelectItem>
                    <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_terms">Términos y Condiciones de Emisión y Servicio</Label>
                <div className="bg-white rounded-md border overflow-hidden">
                  <EditorToolbar editor={editor} />
                  <EditorContent 
                    editor={editor} 
                    className="prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror]:outline-none"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Estos términos se aplicarán automáticamente a nuevas cotizaciones. Usa la barra de herramientas para dar formato al texto.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Backup - Solo Admin */}
          {isAdmin && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Database className="w-5 h-5" />
                  Respaldo de Datos
                </CardTitle>
                <CardDescription>
                  Descarga una copia de seguridad de todos los datos de tu sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    El backup incluye: usuarios, clientes, cotizaciones, configuración, aeropuertos, destinos, aerolíneas y hoteles.
                  </p>
                  <Button 
                    onClick={handleDownloadBackup}
                    disabled={downloadingBackup}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="download-backup-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingBackup ? "Descargando..." : "Descargar Backup"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsAgencia;
